import { assign, isString, merge } from 'lodash';
import { Logger } from 'vue-infra';
import { DATA_STATES } from '@/store/consts';
import WorkatoService from '@/services/workato/WorkatoService';
import workatoTransformer from '@/services/workato/workatoTransformer';
import { NAMESPACE } from '@/common/consts';
import { STATE_KEYS } from './state';
import { actions as baseActions, ACTION_TYPES as BASE_ACTION_TYPES } from '../base-collection/actions';
import { MUTATION_TYPES } from './mutations';

const logger = Logger.getLogger('workato-configuration');

const ACTION_TYPES = assign({}, BASE_ACTION_TYPES, {
  FETCH_WORKATO_TOKEN: 'fetchWorkatoToken',
  SET_WORKATO_TOKEN_REFRESH_FLAG: 'setWorkatoTokenRefreshFlag',
  FETCH_WORKATO_RECIPE: 'fetchWorkatoRecipe',
  FETCH_WORKATO_ADMINS: 'fetchWorkatoAdmins',
  FETCH_EXISTING_WORKATO_ACCOUNT: 'fetchExistingWorkatoAccount',
  FETCH_EXISTING_WORKATO_USER: 'fetchExistingWorkatoUser',
  IMPORT_WORKATO_RECIPE: 'importWorkatoRecipe',
  CREATE_NEW_WORKATO_USER: 'createNewWorkatoUser',
  CREATE_NEW_WORKATO_ACCOUNT: 'createNewWorkatoAccount',
  FETCH_IHUB_CONFIG: 'fetchIhubConfig',
  GET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG: 'getDoNotShowPreBuildRecipeModalFlag',
  SET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG: 'setDoNotShowPreBuildRecipeModalFlag',
});

const actions = assign({}, baseActions, {
  [ACTION_TYPES.FETCH_WORKATO_TOKEN]: async ({ commit, dispatch }) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const workatoToken = await WorkatoService.getWorkatoToken(); // get it as dictionary
      commit(MUTATION_TYPES.SET_WORKATO_TOKEN, workatoToken);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      dispatch(ACTION_TYPES.SET_WORKATO_TOKEN_REFRESH_FLAG, false);
    } catch (error) {
      const workatoTokenResponse = workatoTransformer.transformWorkatoTokenResponse(error.response);
      commit(MUTATION_TYPES.SET_WORKATO_TOKEN, workatoTokenResponse);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato token: ', `${NAMESPACE.IHUB}: workatoToken_actions`, error);
    }
  },
  [ACTION_TYPES.SET_WORKATO_TOKEN_REFRESH_FLAG]: async ({ commit }, flag) => {
    commit(MUTATION_TYPES.SET_WORKATO_TOKEN_REFRESH_FLAG, flag);
  },

  [ACTION_TYPES.FETCH_WORKATO_RECIPE]: async ({ commit }) => {
    commit(MUTATION_TYPES.SET_WORKATO_RECIPE_LOAD_STATE, DATA_STATES.LOADING);
    try {
      const workatoRecipe = await WorkatoService.getWorkatoRecipe();
      commit(MUTATION_TYPES.SET_WORKATO_RECIPE, workatoRecipe);
      commit(MUTATION_TYPES.SET_WORKATO_RECIPE_LOAD_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const workatoRecipe = workatoTransformer.transformWorkatoRecipeResponse(error.response);
      commit(MUTATION_TYPES.SET_WORKATO_RECIPE, workatoRecipe);
      commit(MUTATION_TYPES.SET_WORKATO_RECIPE_LOAD_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato recipe: ', `${NAMESPACE.IHUB}: workatoRecipe_actions`, error);
    }
  },
  [ACTION_TYPES.FETCH_WORKATO_ADMINS]: async ({ commit }) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const workatoAdmins = await WorkatoService.getWorkatoAdmins();
      commit(MUTATION_TYPES.SET_WORKATO_ADMINS, workatoAdmins);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato admins: ', `${NAMESPACE.IHUB}: workatoAdmins_actions`, error);
    }
  },
  [ACTION_TYPES.FETCH_EXISTING_WORKATO_ACCOUNT]: async ({ commit }) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const existingWorkatoAccount = await WorkatoService.getExistingWorkatoAccount();
      commit(MUTATION_TYPES.SET_EXISTING_WORKATO_ACCOUNT, existingWorkatoAccount);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const existingWorkatoAccount = workatoTransformer.transformExistingWorkatoAccountResponse(error.response);
      commit(MUTATION_TYPES.SET_EXISTING_WORKATO_ACCOUNT, existingWorkatoAccount);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato account: ', `${NAMESPACE.IHUB}: workatoAccount_actions`, error);
    }
  },
  [ACTION_TYPES.FETCH_EXISTING_WORKATO_USER]: async ({ commit }) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const existingWorkatoUser = await WorkatoService.getExistingWorkatoUser();
      commit(MUTATION_TYPES.SET_EXISTING_WORKATO_USER, existingWorkatoUser);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const existingWorkatoUser = workatoTransformer.transformExistingWorkatoUserResponse(error.response);
      commit(MUTATION_TYPES.SET_EXISTING_WORKATO_USER, existingWorkatoUser);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato user: ', `${NAMESPACE.IHUB}: workatoUser_actions`, error);
    }
  },
  [ACTION_TYPES.IMPORT_WORKATO_RECIPE]: async ({ commit }) => new Promise(async (resolve, reject) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const importWorkatoRecipe = await WorkatoService.importWorkatoRecipe();
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      resolve(importWorkatoRecipe);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error Import Workato User: ', `${NAMESPACE.IHUB}: importWorkatoRecipe_actions`, error);
      reject(error);
    }
  }),
  [ACTION_TYPES.CREATE_NEW_WORKATO_USER]: async ({ commit }) => new Promise(async (resolve, reject) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const newWorkatoUser = await WorkatoService.createNewWorkatoUser();
      commit(MUTATION_TYPES.SET_NEW_WORKATO_USER, newWorkatoUser);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      resolve(newWorkatoUser);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato user: ', `${NAMESPACE.IHUB}: createWorkatoUser_actions`, error);
      reject(error);
    }
  }),
  [ACTION_TYPES.CREATE_NEW_WORKATO_ACCOUNT]: async ({ commit }) => new Promise(async (resolve, reject) => {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const newWorkatoAccount = await WorkatoService.createNewWorkatoAccount();
      commit(MUTATION_TYPES.SET_NEW_WORKATO_ACCOUNT, newWorkatoAccount);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      resolve(newWorkatoAccount);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching workato account: ', `${NAMESPACE.IHUB}: createWorkatoAccount_actions`, error);
      reject(error);
    }
  }),
  async [ACTION_TYPES.FETCH_IHUB_CONFIG]({ commit }, ihubConfig) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    let ihubConfigResponse = null;
    const inputs = isString(ihubConfig) ?
      [WorkatoService.fetchIhubConfig(ihubConfig)] :
      Object.keys(ihubConfig).map(key => WorkatoService.fetchIhubConfig(key));

    try {
      const results = await Promise.all(inputs);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      ihubConfigResponse = merge({}, ...results.map(({ configName, configValue }) => ({ [configName]: configValue })));
      commit(MUTATION_TYPES.SET_WORKATO_CONFIG_VALUES, ihubConfigResponse);
    } catch (error) {
      logger.error('Error fetching ihub config: ', `${NAMESPACE.IHUB}: ihubConfig_actions`, error);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
    }

    return ihubConfigResponse;
  },
  [ACTION_TYPES.GET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG]: async ({ commit }) => {
    const modalData = await WorkatoService.getWorkatoOnboardingPopup();
    // commit(MUTATION_TYPES.SET_PRE_BUILD_MODAL_FLAG, { ...modalData, value: false });
    commit(MUTATION_TYPES.SET_PRE_BUILD_MODAL_FLAG, modalData);
  },
  [ACTION_TYPES.SET_DO_NOT_SHOW_PRE_BUILD_RECIPE_MODAL_FLAG]: async ({ commit, state }, flag) => {
    const flagCreated = state[STATE_KEYS.DO_NOT_SHOW_PRE_BUILD_RECIPES_MODAL].created;
    const modalData = await WorkatoService.setWorkatoOnboardingPopup(flag, flagCreated);
    commit(MUTATION_TYPES.SET_PRE_BUILD_MODAL_FLAG, modalData);
  },
});

export {
  actions,
  ACTION_TYPES,
};
